import { PeriodPicker } from '@/components/shared/date/PeriodPicker';
import {
  addUtcDays,
  createUTCDate,
  dateToApiFormat,
  localDateToUtcDate,
  utcDateToLocalDate
} from '@/helpers/dateHelper';
import {
  LeaveAllowancePeriod,
  useGetEmployeeLeaveAllowancesQuery,
  useGetEmployeeLeaveUsageQuery,
  useGetOrganisationSettingsQuery
} from '@/services/gql/graphql.generated';
import { useLeaveTypes } from '@/services/leaveManagement/LeaveTypesProvider';
import { useCanEditEmployee } from '@/services/settings/useCanEditEmployee';
import { FC, useEffect, useMemo, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TSTitleCard } from '../../../../components/shared/card/Card';
import { strings } from '../../../../services/translation/strings';
import {
  currentPeriodStart,
  formatPeriod,
  leaveYearStart,
  parseYearParam,
  periodStartDate,
  shiftPeriod,
  YEARLY
} from './leaveBalanceHelpers';
import { TeamMemberAnnualLeaveTable } from './TeamMemberAnnualLeaveTable';

interface TeamMemberAnnualLeaveCardProps {
  employeeId: string;
}

export const TeamMemberAnnualLeaveCard: FC<TeamMemberAnnualLeaveCardProps> = ({ employeeId }) => {
  const canEdit = useCanEditEmployee();
  const leaveTypes = useLeaveTypes();
  const [{ data: settingsData }] = useGetOrganisationSettingsQuery();
  const leaveYearStartMonth = settingsData?.organisationSettings?.leaveYearStartMonth ?? 1;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = parseYearParam(searchParams.get('year'));

  const resolveYearStart = () =>
    urlYear !== undefined
      ? leaveYearStart(urlYear, leaveYearStartMonth)
      : currentPeriodStart(YEARLY, leaveYearStartMonth);

  const [yearStart, setYearStart] = useState(resolveYearStart);
  useEffect(() => {
    setYearStart(resolveYearStart());
  }, [leaveYearStartMonth, urlYear]);

  const changePeriod = (next: Date) => {
    setYearStart(next);
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set('year', String(next.getUTCFullYear()));
        return params;
      },
      { replace: true }
    );
  };

  const yearEnd = useMemo(() => addUtcDays(shiftPeriod(YEARLY, yearStart, 1), -1), [yearStart]);
  const periodStart = dateToApiFormat(yearStart);
  const pickerDate = useMemo(() => utcDateToLocalDate(yearStart), [yearStart]);
  const [{ data: yearlyData }] = useGetEmployeeLeaveAllowancesQuery({
    variables: { employeeId, period: LeaveAllowancePeriod.PerYear, periodStart }
  });
  const allowances = yearlyData?.employeeLeaveAllowances ?? [];
  const [{ data: usageData }] = useGetEmployeeLeaveUsageQuery({
    variables: { employeeId, startDate: periodStart, endDate: dateToApiFormat(yearEnd) }
  });
  const leaveUsage = usageData?.employeeLeaveUsage ?? [];

  const navigate = useNavigate();
  const onEditClick = () => {
    navigate(`/team-member/${employeeId}/edit-leave-balances?year=${yearStart.getUTCFullYear()}`);
  };

  return (
    <TSTitleCard
      className="bg-white"
      title={strings.timeshift.teamMember.annualLeave.cardTitle}
      icon={FiCalendar}
      primaryAction={
        canEdit ? { label: strings.timeshift.teamMember.annualLeave.edit, onClick: onEditClick } : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <PeriodPicker
          date={pickerDate}
          displayFormat="yyyy"
          yearOnly
          yearStartMonth={leaveYearStartMonth}
          formatYear={year => formatPeriod(YEARLY, createUTCDate(year, leaveYearStartMonth - 1, 1))}
          onPick={date => changePeriod(periodStartDate(YEARLY, localDateToUtcDate(date), leaveYearStartMonth))}
          onPrevious={() => changePeriod(shiftPeriod(YEARLY, yearStart, -1))}
          onNext={() => changePeriod(shiftPeriod(YEARLY, yearStart, 1))}
        />
        <TeamMemberAnnualLeaveTable leaveTypes={leaveTypes} allowances={allowances} leaveUsage={leaveUsage} />
      </div>
    </TSTitleCard>
  );
};
